import type { TAccessDecision, TDocRole, TMemberAs } from "@api/access";
import type { TMyDocRole } from "@store/story/types";

export const DOC_ROLES: TDocRole[] = ["editor", "commenter", "viewer"];
/** Guests (people outside the org) are Commenter or Viewer, never Editor. */
const GUEST_ROLES: TDocRole[] = ["commenter", "viewer"];

/**
 * Viewers and commenters get a read-only document. Callers add the SEND_FLOW
 * check (flag off, everyone edits).
 */
export const isReadOnlyDocRole = (
  role: TMyDocRole | undefined,
): role is "viewer" | "commenter" => role === "viewer" || role === "commenter";

export const ROLE_LABEL: Record<TDocRole, string> = {
  editor: "Editor",
  commenter: "Commenter",
  viewer: "Viewer",
};

/** "Collaborating as:" for someone outside the workspace. */
export type TCollabAs = "team" | "guest";

export const rolesFor = (guest: boolean) => (guest ? GUEST_ROLES : DOC_ROLES);

/** Switching an outsider to Guest drops Editor to Commenter. */
export const roleForCollabAs = (as: TCollabAs, role: TDocRole): TDocRole =>
  as === "guest" && role === "editor" ? "commenter" : role;

/** Editor for a guest means making them a Team member (a seat) — ask first. */
export const needsSeatConfirm = (as: TCollabAs, role: TDocRole) =>
  as === "guest" && role === "editor";

/** An outsider made Editor takes a paid seat. */
export const takesSeat = (outsider: boolean, role: TDocRole) =>
  outsider && role === "editor";

/** The `as` the members endpoint wants: org people are plain members. */
export const memberAs = (outsider: boolean, as: TCollabAs): TMemberAs =>
  outsider ? as : "member";

/** One entry per request; Accept all / Reject all send every one. */
export const buildDecisions = (
  requests: { email: string }[],
  accept: boolean,
  role: TDocRole,
  message = "",
): TAccessDecision[] => {
  const trimmed = message.trim();
  return requests.map(({ email }) => ({
    email,
    role,
    accept,
    ...(accept && trimmed ? { message: trimmed } : {}),
  }));
};

/** The word alone ("day" / "days"), for when the number is shown elsewhere. */
export const pluralWord = (n: number, one: string, many = `${one}s`) =>
  n === 1 ? one : many;

export const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${pluralWord(n, one, many)}`;
